import {
  addHours,
  endOfDay,
  isToday,
  isTomorrow,
  isWithinInterval,
  parseISO,
  startOfDay,
  subHours,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { DisplayTimes } from 'types/cms/ToastNotification';

const COUNTDOWN_PREVIEW_HRS = 8;

const getStartTime = (times: DisplayTimes): Date => {
  if (times.dateRange.allDay) {
    return startOfDay(parseISO(times.dateRange.startDate));
  }
  return parseISO(times.dateRange.startDate);
};

const getEndTime = (times: DisplayTimes): Date => {
  if (times.dateRange.allDay) {
    return endOfDay(parseISO(times.dateRange.endDate));
  }
  return parseISO(times.dateRange.endDate);
};

export const isLiveNow = (times: DisplayTimes) => {
  const now = new Date();
  return isWithinInterval(now, {
    start: getStartTime(times),
    end: getEndTime(times),
  });
};

export const isInDisplayWindow = (times: DisplayTimes) => {
  const now = new Date();
  const start = getStartTime(times);
  const end = getEndTime(times);
  return isWithinInterval(now, {
    start: subHours(
      start,
      times?.displayBefore ? times.displayBefore : COUNTDOWN_PREVIEW_HRS,
    ),
    end: times?.displayAfter ? addHours(end, times.displayAfter) : end,
  });
};

export const isInPreviewWindow = (times: DisplayTimes) => {
  const now = new Date();
  const start = getStartTime(times);
  return isWithinInterval(now, {
    start: subHours(
      start,
      times?.displayBefore ? times.displayBefore : COUNTDOWN_PREVIEW_HRS,
    ),
    end: start,
  });
};

export const isInCountdownWindow = (times: DisplayTimes) => {
  const now = new Date();
  const start = getStartTime(times);
  return isWithinInterval(now, {
    start: subHours(start, COUNTDOWN_PREVIEW_HRS),
    end: start,
  });
};

export const isInAfterWindow = (times: DisplayTimes) => {
  const now = new Date();
  const end = getEndTime(times);
  return isWithinInterval(now, {
    start: end,
    end: times?.displayAfter ? addHours(end, times.displayAfter) : end,
  });
};

export const isEnded = (times: DisplayTimes) => {
  const now = new Date();
  const end = getEndTime(times);
  const endTime = times?.displayAfter ? addHours(end, times.displayAfter) : end;
  return now > endTime;
};

export const getUpcomingTimeDisplay = (times: DisplayTimes) => {
  const startTime = getStartTime(times);
  const now = new Date();

  if (now > parseISO(times.dateRange.endDate)) {
    return null;
  }

  if (isToday(startTime)) {
    return formatInTimeZone(
      startTime,
      times.dateRange.timezone,
      "'Today'  h:mm aa  zzz",
    );
  }

  if (isTomorrow(startTime)) {
    return formatInTimeZone(
      startTime,
      times.dateRange.timezone,
      "'Tomorrow'  h:mm aa  zzz",
    );
  }

  return formatInTimeZone(
    startTime,
    times.dateRange.timezone,
    'EEEE  h:mm aa  zzz',
  );
};

export const formatCountdown = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    D: { time: String(days).padStart(2, '0'), unit: 'DD' },
    H: { time: String(hours).padStart(2, '0'), unit: 'HH' },
    M: { time: String(minutes).padStart(2, '0'), unit: 'MM' },
    S: { time: String(seconds).padStart(2, '0'), unit: 'SS' },
  };
};
